import type { I18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import classNames from 'classnames'
import { type ReactChild, type VFC } from 'react'

import IconCaretDown from 'views/components/atoms/Icon/IconCaretDown'
import TextWithIcon from 'views/components/molecules/TextWithIcon'

import type { Product, ProductAttributes, VariantAttribute } from '..'

export const CLIENTSIDE_HOOK_CHILDREN_WRAPPER =
  'productVariantsAttribute-Wrapper'

interface AttributePickerRegionCollapsibleWrapperProps {
  children: ReactChild
  i18n: I18n
  variantAttribute: ProductAttributes
  isSingleAttributeProduct: boolean
  packageSizeAttribute: boolean
  selectedVariant?: Product
  isMasterPage: boolean
}

const AttributePickerRegionCollapsibleWrapper: VFC<
  AttributePickerRegionCollapsibleWrapperProps
> = ({
  children,
  i18n,
  variantAttribute,
  isSingleAttributeProduct,
  packageSizeAttribute,
  selectedVariant,
  isMasterPage,
}) => {
  const itemsThreshold = 6
  const isCollapsible: boolean = variantAttribute.values.length > itemsThreshold
  const hasTileTeaser =
    isSingleAttributeProduct && !packageSizeAttribute && isCollapsible
  const hasPillTeaser = !isSingleAttributeProduct && isCollapsible

  const selectedVariantAttbute = selectedVariant?.variantAttributes?.find(
    (varAttribute: VariantAttribute) => {
      return varAttribute.variantCode === variantAttribute.variantCode
    }
  )
  const hasColor =
    variantAttribute.type.toLowerCase() === 'color' &&
    selectedVariantAttbute?.value.includes('#')

  const childClasses = classNames({
    'o-ProductVariantsAttributePicker-tile-teaser': hasTileTeaser,
    'o-ProductVariantsAttributePicker-pill-teaser': hasPillTeaser,
  })
  const regionColorPickerLabelClasses = classNames(
    'colorPickerColor u-margin-x-small--left',
    {
      'o-collorPickerColor--display--none': !hasColor,
    }
  )

  const collapsibleLabel = (
    <div
      role="button"
      className="o-ProductVariantsAttributePicker-Collapsible u-padding--top"
      data-clientside-hook="attributePicker-collapsible--label"
      data-trans-open={i18n?._('general.label.showNMore', {
        count: variantAttribute.values.length,
      })}
      data-trans-close={i18n?._('general.label.showLess')}
      data-current-state="closed"
    >
      <TextWithIcon icon={<IconCaretDown size="small" />} right centered>
        <Trans
          id="general.label.showNMore"
          values={{
            count: variantAttribute.values.length - itemsThreshold,
          }}
        />
      </TextWithIcon>
    </div>
  )

  const getVariantLabel = (variantAttributes: ProductAttributes) => {
    switch (variantAttributes.variantCode) {
      case 'color_number':
        return <Trans id="variantAttributes.label.color_number" />

      case 'package_size':
        return <Trans id="variantAttributes.label.package_size" />

      default:
        return variantAttributes.label
    }
  }

  let splitColor = ['']
  if (hasColor) {
    splitColor = selectedVariantAttbute?.value
      ? selectedVariantAttbute?.value?.split('#')
      : ['']
  }
  const colorLabel = splitColor[0]
  const colorHex = splitColor[1]

  const getVariantLabelSelectedValue = () => {
    return hasColor ? colorLabel : selectedVariantAttbute?.value
  }

  return (
    <>
      <div
        className="u-font-weight--bold a-p l-flex u-margin-x-small--bottom"
        data-qa-id={`variantAttribute-${variantAttribute.code}`}
      >
        {getVariantLabel(variantAttribute)}:
        <span className="l-flex">
          <span
            style={{
              backgroundColor: colorHex && `#${colorHex}`,
            }}
            className={regionColorPickerLabelClasses}
          ></span>

          <span className="buttonValueDisplay a-h4 u-font-weight--bold u-text-truncate">
            {isMasterPage && variantAttribute.personal
              ? ''
              : getVariantLabelSelectedValue()}
          </span>
        </span>
      </div>
      <div
        data-clientside-hook={CLIENTSIDE_HOOK_CHILDREN_WRAPPER}
        className={childClasses}
      >
        {children}
      </div>
      {(hasTileTeaser || hasPillTeaser) && (
        <div className="o-ProductVariantsAttributePicker-Collapsible-mobile-hidden">
          {collapsibleLabel}
        </div>
      )}
    </>
  )
}
export default AttributePickerRegionCollapsibleWrapper
